import { React, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Landing page for the MCP -> website cart bridge link (opened via the MCP
// App's "Continue to Website Cart" button, through app.openLink() — see
// admin/src/mcp/apps/rough-country-products/app.js). Reads the one-time
// token from the URL, redeems it server-side for the MCP cart_key, keeps
// the same storage contract used by the normal Add to Cart flow, and then
// syncs the resulting CoCart state back into the same browser Express
// session so the existing /api/cart page can render it unchanged.
export default function CartBridge() {
    const mainUrl = process.env.REACT_APP_EXPRESS_APP_URL;
    const navigate = useNavigate();

    const [status, setStatus] = useState("redeeming"); // redeeming | success | error
    const [errorMessage, setErrorMessage] = useState("");

    const syncCartBadge = async () => {
        const response = await axios.get(`${mainUrl}/api/cart`, {
            withCredentials: true,
        });

        const allData = Array.isArray(response?.data?.allData) ? response.data.allData : [];
        const cartItems = allData.reduce((items, item) => {
            const key = item && (item.product_id ?? item.part_number);
            const quantity = Number(item && item.product_quantity) || 0;

            if (key) {
                items[String(key)] = quantity;
            }

            return items;
        }, {});

        if (Object.keys(cartItems).length > 0) {
            localStorage.setItem("totalCartItems", JSON.stringify(cartItems));
        } else {
            localStorage.removeItem("totalCartItems");
        }

        window.dispatchEvent(new Event("cartUpdated"));
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");

        if (!token) {
            setStatus("error");
            setErrorMessage("This link is missing its cart token.");
            return;
        }

        const redeem = async () => {
            try {
                const response = await axios.post(
                    `${mainUrl}/api/cart-bridge/redeem`,
                    {
                        token,
                        website_cart_key: localStorage.getItem("cart-key") || "",
                    },
                    { withCredentials: true }
                );

                const cartKey = response.data && response.data.cart_key;
                if (!cartKey) {
                    throw new Error("No cart_key returned");
                }

                localStorage.setItem("cart-key", cartKey);

                try {
                    await axios.post(
                        `${mainUrl}/api/cart-bridge/sync-session`,
                        { cart_key: cartKey },
                        { withCredentials: true }
                    );

                    try {
                        await syncCartBadge();
                    } catch (badgeSyncError) {
                        console.error("Cart badge sync failed:", badgeSyncError);
                    }
                } catch (syncError) {
                    console.error("Cart bridge session sync failed:", syncError);
                }

                setStatus("success");

                setTimeout(() => {
                    navigate("/add-cart");
                }, 1200);
            } catch (error) {
                console.error("Cart bridge redeem failed:", error);

                // A partial-merge failure (HTTP 502) still returns a usable
                // cart_key — items that made it in before the failure are
                // real and should not be abandoned, so still adopt it.
                const data = error.response && error.response.data;
                if (data && data.cart_key) {
                    localStorage.setItem("cart-key", data.cart_key);
                }

                setStatus("error");
                setErrorMessage(
                    (data && data.error) ||
                        "This link has expired or already been used. Please try again from the assistant."
                );
            }
        };

        redeem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container py-5 text-center" style={{ maxWidth: 480 }}>
            {status === "redeeming" && (
                <>
                    <h4 className="mb-3">Adding items to your cart…</h4>
                    <p className="text-muted">Please wait a moment.</p>
                </>
            )}

            {status === "success" && (
                <>
                    <h4 className="mb-3">Added to your cart!</h4>
                    <p className="text-muted">Redirecting you to your cart…</p>
                </>
            )}

            {status === "error" && (
                <>
                    <h4 className="mb-3">Something went wrong</h4>
                    <p className="text-muted mb-4">{errorMessage}</p>
                    <button className="btn btn-danger" onClick={() => navigate("/add-cart")}>
                        Go to My Cart
                    </button>
                </>
            )}
        </div>
    );
}
